// Persistent personal display profile. Auth0 remains the login authority.
import React, { useCallback, useEffect, useState } from "react";
import "./Profile.css";

const empty = { first_name: "", last_name: "", preferred_name: "" };

export default function Profile({ apiBaseUrl, tenantId, userId, getAccessToken }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const request = useCallback(async (method, body) => {
    const token = await getAccessToken();
    const response = await fetch(`${apiBaseUrl}/account/profile`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenantId,
        "X-User-Id": userId,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error(`Profile request failed (${response.status})`);
    return response.json();
  }, [apiBaseUrl, tenantId, userId, getAccessToken]);

  const loadPhoto = useCallback(async (revision) => {
    if (!revision) { setPhotoUrl(""); return; }
    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBaseUrl}/account/profile/photo`, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenantId, "X-User-Id": userId },
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Photo could not be loaded");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPhotoUrl(previous => { if (previous) URL.revokeObjectURL(previous); return url; });
    } catch { setPhotoUrl(""); }
  }, [apiBaseUrl, tenantId, userId, getAccessToken]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  const changePhoto = async (file) => {
    if (!file || photoBusy) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPG, PNG, or WebP photo."); return;
    }
    if (file.size > 3 * 1024 * 1024) { setError("Photo must be 3 MB or smaller."); return; }
    setPhotoBusy(true); setError(""); setMessage("");
    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBaseUrl}/account/profile/photo`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenantId, "X-User-Id": userId,
                   "Content-Type": file.type }, body: file,
      });
      if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
      const data = await response.json();
      setProfile(data); await loadPhoto(data.photo_reference);
      setMessage("Profile photo saved.");
      window.dispatchEvent(new Event("oasys:personal-profile-updated"));
    } catch (e) { setError(e.message || "Unable to save photo."); }
    finally { setPhotoBusy(false); }
  };
  const removePhoto = async () => {
    if (photoBusy) return;
    setPhotoBusy(true); setError(""); setMessage("");
    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBaseUrl}/account/profile/photo`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenantId, "X-User-Id": userId },
      });
      if (!response.ok) throw new Error(`Photo removal failed (${response.status})`);
      setProfile(await response.json()); setPhotoUrl("");
      setMessage("Profile photo removed.");
      window.dispatchEvent(new Event("oasys:personal-profile-updated"));
    } catch (e) { setError(e.message || "Unable to remove photo."); }
    finally { setPhotoBusy(false); }
  };

  const reload = useCallback(async () => {
    if (!tenantId || !userId || !getAccessToken || !apiBaseUrl) return;
    setLoading(true);
    setError("");
    try {
      const data = await request("GET");
      setProfile(data);
      await loadPhoto(data.photo_reference);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        preferred_name: data.preferred_name || "",
      });
    } catch (e) {
      setError(e.message || "Unable to load your profile.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, apiBaseUrl, getAccessToken, request, loadPhoto]);

  useEffect(() => {
    setProfile(null);
    setMessage("");
    if (tenantId && userId) reload();
    else setLoading(false);
  }, [tenantId, userId, reload]);

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request("PATCH", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        preferred_name: form.preferred_name.trim(),
      });
      setProfile(data);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        preferred_name: data.preferred_name || "",
      });
      setEditing(false);
      setMessage("Profile saved. Your welcome greeting has been updated.");
      window.dispatchEvent(new Event("oasys:personal-profile-updated"));
    } catch (e) {
      setError(e.message || "Unable to save your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId || !userId) return <div className="settings-inline-state">An active OASYS account is required to edit your profile.</div>;
  if (loading) return <div className="settings-inline-state">Loading profile…</div>;
  if (!profile) return <div className="settings-inline-state settings-inline-error">{error || "Profile unavailable."}</div>;

  const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.display_name || "My profile";
  return (
    <div className="settings-profile-shell">
      <section className="settings-profile-card">
        <div className="settings-profile-card-header">
          <div><h3>Personal profile</h3><p>Your display information is saved to your OASYS account and persists between logins.</p></div>
          {!editing && <button type="button" className="settings-secondary-button" onClick={() => { setEditing(true); setMessage(""); }}>Edit profile</button>}
        </div>
        {message && <p role="status">{message}</p>}
        {error && <p role="alert" className="settings-inline-error">{error}</p>}
        {editing ? (
          <form className="settings-profile-form" onSubmit={save}>
            {[["first_name", "First name"], ["last_name", "Last name"], ["preferred_name", "Preferred name for greeting"]].map(([key, label]) => (
              <label key={key}><span>{label}</span><input value={form[key]} maxLength={80} onChange={(e) => setForm(old => ({...old, [key]: e.target.value}))} disabled={saving} /></label>
            ))}
            <div className="settings-profile-actions">
              <button type="submit" className="settings-primary-button" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
              <button type="button" className="settings-secondary-button" disabled={saving} onClick={() => { setEditing(false); setError(""); }}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="settings-profile-grid">
            <div><span>Full name</span><strong>{name}</strong></div>
            <div><span>Preferred name</span><strong>{profile.preferred_name || "Not set"}</strong></div>
            <div className="settings-profile-grid-wide"><span>Sign-in email</span><strong className="settings-profile-readonly-email">{profile.email || "—"}</strong></div>
            <div className="settings-profile-grid-wide"><span>Profile photo</span>
              <div className="settings-profile-photo-row">
                {photoUrl ? <img className="settings-profile-photo-preview" src={photoUrl} alt="Your profile" /> :
                  <div className="settings-profile-photo-fallback" aria-label="No profile photo">{String(profile.preferred_name || profile.first_name || name).charAt(0).toUpperCase()}</div>}
                <div className="settings-profile-photo-actions">
                  <label className="settings-secondary-button settings-photo-upload-label">
                    {photoBusy ? "Working…" : profile.photo_reference ? "Replace photo" : "Upload photo"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" disabled={photoBusy}
                      onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) changePhoto(file); }} />
                  </label>
                  {profile.photo_reference && <button type="button" className="settings-secondary-button" disabled={photoBusy} onClick={removePhoto}>Remove photo</button>}
                  <small>JPG, PNG, or WebP. Maximum 3 MB.</small>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
